import * as React from 'react'
import { Link, useParams } from 'react-router'
import { Archive, ArrowLeft, Eye, Loader2, Pencil, PauseCircle, PlayCircle, RotateCcw } from 'lucide-react'
import type { OpsTenantDetailDto } from '@lib/dto/team.dto'
import type { ApiKeyDto } from '@lib/dto/tenant-settings.dto'
import { BILLING } from '@lib/constants/billing'
import { formatCents, formatMicros, microsToWholeCents, refundQuote } from '@lib/helpers/money'
import { closeTenantInputSchema, suspendTenantInputSchema } from '@lib/input/tenant.input'
import { uuidParamSchema, type Uuid } from '@lib/validators/params'
import { useAuth } from '@/auth/AuthContext'
import { ActivityTable } from '@/components/activity/ActivityTable'
import { OpsBillingCard } from '@/components/billing/OpsBillingCard'
import { ApiKeyTable } from '@/components/keys/ApiKeyTable'
import { RevokeKeyDialog } from '@/components/keys/RevokeKeyDialog'
import { BrandingSummary } from '@/components/tenants/BrandingSummary'
import { TenantStatusBadge } from '@/components/tenants/TenantStatusBadge'
import { PageHeader } from '@/components/PageHeader'
import { ErrorState, LoadingState } from '@/components/PageState'
import { InvitationList } from '@/components/team/InvitationList'
import { InviteForm } from '@/components/team/InviteForm'
import { MemberTable } from '@/components/team/MemberTable'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Field, fieldMessageId } from '@/components/ui/field'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/toast'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useOpsBilling } from '@/hooks/useOpsBilling'
import { useOpsTenant, useOpsTenantActions, useOpsTenantActivity, useOpsTenantKeys } from '@/hooks/useOpsTenants'

type Actions = ReturnType<typeof useOpsTenantActions>

interface TenantDialogProps {
  tenant: OpsTenantDetailDto
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface SuspendDialogProps extends TenantDialogProps {
  suspend: Actions['suspend']
}

interface ReactivateDialogProps extends TenantDialogProps {
  reactivate: Actions['reactivate']
}

interface CloseDialogProps extends TenantDialogProps {
  close: Actions['close']
}

interface ReopenDialogProps extends TenantDialogProps {
  reopen: Actions['reopen']
}

type TenantDialog = 'suspend' | 'reactivate' | 'close' | 'reopen'

interface TenantProps {
  tenant: OpsTenantDetailDto
}

/** Asks why, then suspends: API keys stop working and the dashboard goes read-only. */
function SuspendDialog({ tenant, open, onOpenChange, suspend }: SuspendDialogProps) {
  const [reason, setReason] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const parsed = suspendTenantInputSchema.safeParse({ reason })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Give a reason')
      return
    }
    setError(null)
    try {
      await suspend.mutateAsync(parsed.data.reason)
      toast.success(`${tenant.name} suspended`)
      setReason('')
      onOpenChange(false)
    } catch (failure) {
      setError(apiErrorMessage(failure, 'Could not suspend the tenant.'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <DialogHeader>
            <DialogTitle>Suspend {tenant.name}?</DialogTitle>
            <DialogDescription>
              Their API keys stop working and their dashboard becomes read-only. Envelopes already sent can still be
              signed, and webhooks still deliver. You can reactivate at any time.
            </DialogDescription>
          </DialogHeader>
          <Field label="Reason" htmlFor="suspend-reason" error={error} hint="Staff only. Owners see that the organization was suspended, not why.">
            <Textarea
              id="suspend-reason"
              rows={3}
              value={reason}
              aria-invalid={error ? true : undefined}
              aria-describedby={fieldMessageId('suspend-reason')}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={suspend.isPending}>
              {suspend.isPending ? <Loader2 className="animate-spin" /> : null}
              Suspend
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Confirms reactivation: keys work again and owners can manage the dashboard. */
function ReactivateDialog({ tenant, open, onOpenChange, reactivate }: ReactivateDialogProps) {
  const [error, setError] = React.useState<string | null>(null)

  async function confirm(): Promise<void> {
    setError(null)
    try {
      await reactivate.mutateAsync()
      toast.success(`${tenant.name} reactivated`)
      onOpenChange(false)
    } catch (failure) {
      setError(apiErrorMessage(failure, 'Could not reactivate the tenant.'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reactivate {tenant.name}?</DialogTitle>
          <DialogDescription>
            Their API keys work again right away and owners can manage their dashboard.
          </DialogDescription>
        </DialogHeader>
        {error ? <p className="text-body text-danger">{error}</p> : null}
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => void confirm()} disabled={reactivate.isPending}>
            {reactivate.isPending ? <Loader2 className="animate-spin" /> : null}
            Reactivate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** What closing does to the organization's credit, when it has paid credit to refund. */
function ClosingCreditLine({ tenantId }: { tenantId: Uuid }) {
  const billing = useOpsBilling(tenantId)
  if (!billing.data || billing.data.paidMicros === 0) return null
  const refundableCents = microsToWholeCents(billing.data.paidMicros)
  const net = formatCents(refundQuote(refundableCents).netCents)
  const granted = billing.data.grantedMicros > 0 ? ` ${formatMicros(billing.data.grantedMicros)} of granted credit is not refunded.` : ''
  return (
    <li>{`Closing refunds ${formatCents(refundableCents)} of paid credit less the ${BILLING.refundFeePercent}% fee (${net}) to its card payments.${granted}`}</li>
  )
}

/**
 * Asks why, then closes the organization for good. The consequences are
 * spelled out here because, unlike suspending, most of them can't be undone.
 */
function CloseDialog({ tenant, open, onOpenChange, close }: CloseDialogProps) {
  const [reason, setReason] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const parsed = closeTenantInputSchema.safeParse({ reason })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Give a reason')
      return
    }
    setError(null)
    try {
      await close.mutateAsync(parsed.data.reason)
      toast.success(`${tenant.name} closed`)
      setReason('')
      onOpenChange(false)
    } catch (failure) {
      setError(apiErrorMessage(failure, 'Could not close the organization.'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <DialogHeader>
            <DialogTitle>Close {tenant.name}?</DialogTitle>
            <DialogDescription>For an organization leaving PixelSign for good.</DialogDescription>
          </DialogHeader>
          <ul className="list-disc space-y-1 pl-5 text-body">
            <li>Every API key and pending invitation is revoked. Reopening doesn&apos;t bring them back.</li>
            <li>Envelopes waiting for signatures are voided, and their signers are emailed that the organization closed.</li>
            <li>Members can no longer open the dashboard, and signing links stop working.</li>
            <li>Staff can still download documents for 90 days. Then the files are deleted; the audit trail stays.</li>
            <ClosingCreditLine tenantId={tenant.id} />
          </ul>
          <Field label="Reason" htmlFor="close-reason" error={error} hint="Kept in the organization's activity log.">
            <Textarea
              id="close-reason"
              rows={3}
              value={reason}
              aria-invalid={error ? true : undefined}
              aria-describedby={fieldMessageId('close-reason')}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="destructive" disabled={close.isPending}>
              {close.isPending ? <Loader2 className="animate-spin" /> : null}
              Close organization
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** Confirms reopening a closed organization before its purge. */
function ReopenDialog({ tenant, open, onOpenChange, reopen }: ReopenDialogProps) {
  const [error, setError] = React.useState<string | null>(null)

  async function confirm(): Promise<void> {
    setError(null)
    try {
      await reopen.mutateAsync()
      toast.success(`${tenant.name} reopened`)
      onOpenChange(false)
    } catch (failure) {
      setError(apiErrorMessage(failure, 'Could not reopen the organization.'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reopen {tenant.name}?</DialogTitle>
          <DialogDescription>
            Members can sign in again and the file purge is cancelled. Their API keys and invitations stay revoked, so
            owners create new keys, and voided envelopes stay voided.
          </DialogDescription>
        </DialogHeader>
        {error ? <p className="text-body text-danger">{error}</p> : null}
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => void confirm()} disabled={reopen.isPending}>
            {reopen.isPending ? <Loader2 className="animate-spin" /> : null}
            Reopen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/** Ends free text with a period unless it already ends a sentence. */
function sentence(text: string): string {
  return /[.!?]$/.test(text) ? text : `${text}.`
}

function ClosedNotice({ tenant }: TenantProps) {
  const closedAt = tenant.closedAt ? ` ${new Date(tenant.closedAt).toLocaleString()}` : ''
  return (
    <div role="status" className="flex items-start gap-2 rounded-lg border border-border bg-muted px-3 py-2 text-body">
      <Archive className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <span>
        <strong className="font-semibold">
          Closed{closedAt}
          {tenant.closedReason ? ':' : '.'}
        </strong>{' '}
        {tenant.closedReason ? `${sentence(tenant.closedReason)} ` : ''}
        {tenant.purgedAt
          ? `Its envelope files were deleted ${new Date(tenant.purgedAt).toLocaleDateString()}; the audit trail remains.`
          : tenant.purgeAfter
            ? `Staff can download its documents until ${new Date(tenant.purgeAfter).toLocaleDateString()}, when the files are deleted.`
            : ''}
      </span>
    </div>
  )
}

function SuspendedNotice({ tenant }: TenantProps) {
  return (
    <div
      role="status"
      className="flex items-start gap-2 rounded-lg border border-warning-border bg-warning-subtle px-3 py-2 text-body text-warning"
    >
      <PauseCircle className="mt-0.5 size-4 shrink-0" />
      <span>
        <strong className="font-semibold">
          Suspended{tenant.suspendedAt ? ` ${new Date(tenant.suspendedAt).toLocaleString()}` : ''}
          {tenant.suspendedReason ? ':' : '.'}
        </strong>{' '}
        {tenant.suspendedReason ? `${sentence(tenant.suspendedReason)} ` : ''}API keys are disabled and the dashboard is
        read-only.
      </span>
    </div>
  )
}

/**
 * One tenant for staff. ADMIN edits the profile, suspends or reactivates,
 * closes or reopens,
 * views the dashboard as the tenant (read-only), and invites owners; members
 * are read-only here, since owners manage their own team. Any staff role sees
 * the activity log.
 */
export function OpsTenantDetailPage() {
  const { id: rawId } = useParams()
  const parsedId = uuidParamSchema.safeParse(rawId)
  const id = parsedId.success ? parsedId.data : null
  const { user } = useAuth()
  const query = useOpsTenant(id)
  const activity = useOpsTenantActivity(id)
  const apiKeys = useOpsTenantKeys(id)
  const actions = useOpsTenantActions(id)
  const [busyId, setBusyId] = React.useState<Uuid | null>(null)
  const [actionError, setActionError] = React.useState<string | null>(null)
  const [dialog, setDialog] = React.useState<TenantDialog | null>(null)
  const [revokingKey, setRevokingKey] = React.useState<ApiKeyDto | null>(null)
  const isAdmin = user?.role === 'ADMIN'

  async function run<T>(invitationId: Uuid, action: () => Promise<T>, success: string): Promise<void> {
    setBusyId(invitationId)
    setActionError(null)
    try {
      await action()
      toast.success(success)
    } catch (error) {
      setActionError(apiErrorMessage(error))
    } finally {
      setBusyId(null)
    }
  }

  function setDialogOpen(kind: TenantDialog) {
    return (open: boolean) => setDialog(open ? kind : null)
  }

  function renderKeys() {
    if (apiKeys.isLoading) return <LoadingState />
    if (apiKeys.isError || !apiKeys.data) {
      return <ErrorState message={apiErrorMessage(apiKeys.error, 'Could not load API keys.')} />
    }
    if (apiKeys.data.keys.length === 0) {
      return <p className="text-body text-muted-foreground">No API keys. Owners create them in their dashboard.</p>
    }
    return <ApiKeyTable keys={apiKeys.data.keys} canRevoke={() => isAdmin} onRevoke={setRevokingKey} />
  }

  function renderActivity() {
    if (activity.isLoading) return <LoadingState />
    if (activity.isError || !activity.data) {
      return <ErrorState message={apiErrorMessage(activity.error, 'Could not load the activity.')} />
    }
    return <ActivityTable events={activity.data.events} />
  }

  function renderHeaderActions(tenant: OpsTenantDetailDto) {
    const ownerBadge = tenant.ownerCount === 0 ? <Badge tone="warning">No owner</Badge> : null
    // Every staff role may view the dashboard read-only; only ADMIN changes the tenant.
    const viewDashboard = (
      <Button asChild>
        <Link to={`/ops/tenants/${tenant.id}/view`}>
          <Eye />
          View dashboard
        </Link>
      </Button>
    )
    if (!isAdmin) {
      return (
        <>
          {ownerBadge}
          {viewDashboard}
        </>
      )
    }
    if (tenant.status === 'closed') {
      return (
        <>
          {tenant.purgedAt ? null : (
            <Button variant="secondary" onClick={() => setDialog('reopen')}>
              <RotateCcw />
              Reopen
            </Button>
          )}
          {viewDashboard}
        </>
      )
    }
    const suspended = tenant.status === 'suspended'
    return (
      <>
        {ownerBadge}
        {suspended ? (
          <Button variant="secondary" onClick={() => setDialog('reactivate')}>
            <PlayCircle />
            Reactivate
          </Button>
        ) : (
          <Button variant="destructive-quiet" onClick={() => setDialog('suspend')}>
            <PauseCircle />
            Suspend
          </Button>
        )}
        <Button variant="destructive-quiet" onClick={() => setDialog('close')}>
          <Archive />
          Close
        </Button>
        <Button variant="secondary" asChild>
          <Link to={`/ops/tenants/${tenant.id}/edit`}>
            <Pencil />
            Edit
          </Link>
        </Button>
        {viewDashboard}
      </>
    )
  }

  const backLink = (
    <Link to="/ops/tenants" className="inline-flex items-center gap-1 hover:text-foreground">
      <ArrowLeft className="size-3.5" />
      Tenants
    </Link>
  )

  if (id === null) {
    return (
      <>
        <PageHeader eyebrow={backLink} title="Tenant" />
        <ErrorState message="That organization link is not valid." />
      </>
    )
  }

  if (query.isLoading) return <LoadingState />
  if (query.isError || !query.data) {
    return (
      <>
        <PageHeader eyebrow={backLink} title="Tenant" />
        <ErrorState message={apiErrorMessage(query.error, 'Could not load the tenant.')} />
      </>
    )
  }

  const tenant = query.data
  return (
    <>
      <PageHeader
        eyebrow={backLink}
        title={
          <span className="inline-flex items-center gap-2">
            {tenant.name}
            {tenant.status === 'active' ? null : <TenantStatusBadge status={tenant.status} />}
          </span>
        }
        description={<span className="font-mono text-caption">{tenant.slug}</span>}
        actions={renderHeaderActions(tenant)}
      />
      {actionError ? <ErrorState message={actionError} className="mb-6" /> : null}

      <div className="space-y-8">
        {tenant.status === 'suspended' ? <SuspendedNotice tenant={tenant} /> : null}
        {tenant.status === 'closed' ? <ClosedNotice tenant={tenant} /> : null}

        <BrandingSummary profile={tenant} />

        <OpsBillingCard tenant={tenant} isAdmin={isAdmin} />

        {isAdmin && tenant.status !== 'closed' ? (
          <Card>
            <CardHeader>
              <CardTitle>Invite to {tenant.name}</CardTitle>
              <CardDescription>
                {tenant.ownerCount === 0
                  ? 'This tenant has no owner yet. Invite one so they can manage their own team.'
                  : 'Use this to add an owner when a tenant has lost theirs; owners invite everyone else.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <InviteForm defaultRole="owner" onInvite={(input) => actions.invite.mutateAsync(input)} />
            </CardContent>
          </Card>
        ) : null}

        <section className="space-y-3">
          <h2 className="text-title font-semibold">
            Members <span className="font-normal text-muted-foreground">{tenant.team.members.length}</span>
          </h2>
          {tenant.team.members.length === 0 ? (
            <p className="text-body text-muted-foreground">No members yet.</p>
          ) : (
            <MemberTable members={tenant.team.members} canManage={false} busyUserId={null} />
          )}
        </section>

        <section className="space-y-3">
          <h2 className="text-title font-semibold">Pending invitations</h2>
          <InvitationList
            invitations={tenant.team.invitations}
            canManage={isAdmin}
            busyId={busyId}
            onResend={(invitationId) =>
              void run(invitationId, () => actions.resend.mutateAsync(invitationId), 'Invitation re-sent')
            }
            onRevoke={(invitationId) =>
              void run(invitationId, () => actions.revoke.mutateAsync(invitationId), 'Invitation revoked')
            }
          />
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-title font-semibold">API keys</h2>
            <p className="text-body text-muted-foreground">
              Staff can revoke a key (for example, one that leaked) but never create one or see a secret.
            </p>
          </div>
          {renderKeys()}
        </section>

        <section className="space-y-3">
          <h2 className="text-title font-semibold">Activity</h2>
          {renderActivity()}
        </section>
      </div>

      {isAdmin ? (
        <>
          <SuspendDialog
            tenant={tenant}
            open={dialog === 'suspend'}
            onOpenChange={setDialogOpen('suspend')}
            suspend={actions.suspend}
          />
          <ReactivateDialog
            tenant={tenant}
            open={dialog === 'reactivate'}
            onOpenChange={setDialogOpen('reactivate')}
            reactivate={actions.reactivate}
          />
          <CloseDialog tenant={tenant} open={dialog === 'close'} onOpenChange={setDialogOpen('close')} close={actions.close} />
          <ReopenDialog
            tenant={tenant}
            open={dialog === 'reopen'}
            onOpenChange={setDialogOpen('reopen')}
            reopen={actions.reopen}
          />
          <RevokeKeyDialog
            apiKey={revokingKey}
            onClose={() => setRevokingKey(null)}
            onRevoke={(keyId) => actions.revokeKey.mutateAsync(keyId)}
          />
        </>
      ) : null}
    </>
  )
}
